import { HttpResponse } from '@angular/common/http';
import { Injectable, SecurityContext, inject, signal } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { DeviceService } from '@frontend/vanilla/core';
import { TrackingValueService } from '@payments/common-utils';
import { map } from 'rxjs/operators';

import { AppConstants, ProductType, QDType } from '../config/appConstants';
import { UrlConstants } from '../config/urlConstants';
import { DepositInfo, DepositSubmitRequest } from '../model/depositInfo.model';
import { QDInstrument } from '../model/qdInstrument.model';
import { ErrorCode, ErrorTemplate, ResponseStatus } from '../shared/error.constants';
import { BehaviorDataService } from './behaviorData.service';
import { BetslipQdMessageService } from './betslip-qd-message.service';
import { BetslipQdApiService } from './betslipqd-api.service';
import { DataService } from './data.service';
import { DepositRedirectService } from './depositRedirect.service';
import { EventService } from './event.service';
import { GtmService } from './gtm.service';
import { LoaderService } from './loader.service';
import { OptionListService } from './optionList.service';
import { StakeAmountService } from './stakeAmount.service';
import { StoreService } from './store.service';
import { UtilService } from './util.service';

const iframeCode = `
<form name="threeDSMethodDataForm" id="threeDSMethodDataForm" method="POST">
<input type="hidden" name="threeDSMethodData" id="threeDSMethodData" />
</form>
`;

@Injectable({
    providedIn: 'root',
})
export class DepositService {
    private store = inject(StoreService);
    private utilService = inject(UtilService);
    private apiService = inject(BetslipQdApiService);
    private dataService = inject(DataService);
    private deviceService = inject(DeviceService);
    private depositRedirectService = inject(DepositRedirectService);
    private stakeAmountService = inject(StakeAmountService);
    private optionListService = inject(OptionListService);
    private behaviorData = inject(BehaviorDataService);
    private sanitizer = inject(DomSanitizer);
    private gtmService = inject(GtmService);
    private loaderService = inject(LoaderService);
    private eventService = inject(EventService);
    private betslipqdMessageService = inject(BetslipQdMessageService);
    private trackingValueService = inject(TrackingValueService);
    _conversionRates: any;
    _depositInfo: DepositInfo;
    _submitResponse: any;
    isDataLoading = signal<boolean>(false);
    showDepositInput = signal<boolean>(false);
    showDepositFailure = signal<boolean>(false);
    showDepositSuccess = signal<boolean>(false);
    _qdType: string;
    currencyConversionMap: any = {};

    arcDepositBlockStatus: any;
    billingDescriptor: any;
    paypalDirectBadFunding: any = null;
    exchangeRate: number;
    isPaypalBadFunding: boolean = false;
    currency: any; // TODO: check and remove it
    estimatedReturn: any; // TODO: check and remove it
    isBetSuspended: any; // TODO: check and remove it
    isPriceChanged: any; // TODO: check and remove it
    successMessageData: any = {
        eventName: 'FundsAdded',
        eventData: {
            success: true,
        },
    };
    fundsNeeded: any;
    oldHeight: number = 200;
    isConversionMapLoaded: boolean = false;
    conversionMap: any;
    showBonusInput: boolean = false;
    prefillInDecimal: boolean;
    depositSubmitRequest: any;
    gaSessionId: string | undefined;
    gaClientId: string | undefined;
    constructor() {
        this.handleResponse = this.handleResponse.bind(this);
        this.handleRedirectionResponse = this.handleRedirectionResponse.bind(this);
        this.initializeDepositInfo();
        this.prefillAmountRequiredToMeetStake = this.prefillAmountRequiredToMeetStake.bind(this);
    }

    set conversionRates(_conversionRates: any) {
        this._conversionRates = _conversionRates;
    }

    get conversionRates() {
        return this._conversionRates;
    }

    set depositInfo(_depositInfo: DepositInfo) {
        this.dataService.depositInfo = _depositInfo;
        this._depositInfo = _depositInfo;
    }

    get depositInfo() {
        return this._depositInfo;
    }

    set submitResponse(_submitResponse: any) {
        this._submitResponse = _submitResponse;
    }

    get submitResponse() {
        return this._submitResponse;
    }

    set QDType(_qdType: string) {
        this._qdType = _qdType;
    }

    get QDType() {
        return this._qdType;
    }

    cardFormNGPGErrorStatus: boolean;
    initializeDepositInfo() {
        if (!this.depositInfo) {
            this.depositInfo = new DepositInfo();
            this.depositInfo.amountInfo = {};
            this.depositInfo.bonusInfo = {};
            this.depositInfo.browserInfo = this.setBrowserInfo();
            this.depositInfo.paymentAccountInfo = {};
            this.depositInfo.productPreference = ProductType.SPORTSBOOK;
            this.depositInfo.details = {};
        }
    }
    loadConversionMap() {
        const sessionInfo = this.dataService.sessionInfo;
        this.conversionMap = this.conversionRates;
        this.isConversionMapLoaded = true;
        this.stakeAmountService.intialize();
        if (this.conversionMap) {
            if (sessionInfo?.stake && !this.stakeAmountService?.isStakeAmountServiceInitialized()) {
                const isGvc = this.QDType === QDType.GVCBETSLIP;
                this.stakeAmountService.data.stake = isGvc ? sessionInfo.stake : +(Math.round(+sessionInfo.stake * 100) / 100) / 100;
                this.stakeAmountService.retrieveAmountRequiredToMeetStake(this.prefillAmountRequiredToMeetStake);
            } else {
                this.prefillAmountRequiredToMeetStake();
            }
        }
    }
    prefillAmountRequiredToMeetStake() {
        const amountToPopulate = this.stakeAmountService.getAmountToBePopulated(null, this.conversionRates);
        this.prefillInDecimal = true;
        const roundUpTo = this.QDType === QDType.GVCBETSLIP ? 'ceil' : 'none';
        this.prefillNavAmount(this.convertAmountToTxnCurrency(amountToPopulate, roundUpTo).toString());
    }

    setBrowserInfo() {
        const browserInfo: any = {};
        browserInfo.browserLanguage = window.navigator.language.substring(0, 2);
        browserInfo.browserScreenHeight = screen.availHeight;
        browserInfo.browserScreenWidth = screen.availWidth;
        browserInfo.browserTimeZone = new Date().getTimezoneOffset();
        return browserInfo;
    }

    getAmountInAccountCurrency(): any {
        return this.getAmountInRequiredCurrency(this.depositInfo?.amountInfo?.nativeAmountString, false);
    }
    private getAmountInRequiredCurrency(value: number = 0, isTxnCurrency: boolean): any {
        const txnAmount = parseFloat(value || this.depositInfo?.amountInfo?.nativeAmountString);
        const txnCurrency = this.depositInfo?.amountInfo?.nativeCurrency;
        const accCurrency = this.optionListService?.selectedOption?.paymentMethodInfo?.minDepositLimit?.currencyCode;
        if (isNaN(txnAmount)) {
            return '0';
        }
        const key = isTxnCurrency
            ? `${accCurrency?.toUpperCase()}_TO_${txnCurrency?.toUpperCase()}`
            : `${txnCurrency?.toUpperCase()}_TO_${accCurrency?.toUpperCase()}`;
        this.exchangeRate = 1;
        let convertedValue = '0.00';
        if (txnCurrency?.toUpperCase() !== accCurrency?.toUpperCase()) {
            this.exchangeRate = this.conversionRates[key];
        }
        convertedValue = (txnAmount * this.exchangeRate).toFixed(2);
        const convertedAmount: any = parseFloat(convertedValue);
        this.behaviorData.amountInAccCurrency.next(convertedAmount);
        this.depositInfo.amountCharged = parseFloat(convertedAmount + this.getFeeAmount(convertedAmount)).toFixed(2);
        return convertedValue;
    }

    getAmountInTxnCurrency(value: number = 0): string {
        return this.getAmountInRequiredCurrency(value, true);
    }
    affMaxLossLimitInterceptorCall(status: any) {
        return this.apiService.post(UrlConstants.AFF_MAXLOSS_TOGGLE_INTERCEPTOR + `?status=${status}`, '').subscribe();
    }

    getFeeAmount(amount: any) {
        const fee = parseFloat(this.optionListService?.selectedOption?.paymentMethodInfo?.formattedFee);
        const feeType = this.optionListService?.selectedOption?.paymentMethodInfo?.feeType;

        const fee_amount = feeType === 'abs' ? fee : (amount * fee) / 100;
        return parseFloat(fee_amount.toFixed(2));
    }

    showRequiredView(amountRequiredToMeetStake: any) {
        if (amountRequiredToMeetStake > 0) {
            this.behaviorData.isFullStakeAmountNotMet.next(true);
        } else {
            this.behaviorData.isFullStakeAmountNotMet.next(false);
            this.showDepositInput.set(true);
            this.showDepositSuccess.set(true);
            this.loaderService.depositRedirectInProgress.set(false);
            if (this.QDType === QDType.GVCBETSLIP) {
                const response = this.dataService.data;
                const url = `bwinex://?event=success&type=${response?.paymentMethod}&currency=${response?.amount?.currencyCode}&amount=${response?.amount?.value}&sso=${this.dataService.sessionInfo?.sessionKey}`;
                this.eventService.raiseDepositEvent(url);
            }
        }
        if (this.QDType == QDType.GVCBETSLIP) {
            this.optionListService.depositAvailable.set(false);
        }
        this.eventService.raiseUpdateBalanceEvent();
        this.eventService.raiseCloseEvent(false);
    }

    handleResponse(response: any) {
        this.submitResponse = response;
        this.dataService.data = response;
        if (response?.status === ResponseStatus.SUCCESS) {
            response.quickJourney = true;
            this.successMessageData.eventData.success = true;
            this.postSuccessMessageToParent(this.getAmountInAccountCurrency(), response?.quickJourney);
            this.store.setAdaInteractionMessage(false);
            this.store.setErrorCode(0);
            if (this.QDType == QDType.GVCBETSLIP) {
                this.optionListService.depositAvailable.set(false);
            }
            this.showDepositSuccess.set(true);
            this.loaderService.depositRedirectInProgress.set(false);
            this.showDepositInput.set(false);
            this.optionListService.reversalExists = false;
            this.stakeAmountService.retrieveAmountRequiredToMeetStake(this.showRequiredView.bind(this));
        } else if (response?.status === ResponseStatus.POST_REDIRECT) {
            this.loaderService.depositRedirectInProgress.set(true);
            this.depositRedirectService.depositSubmitResponse = response;
            this.billingDescriptor = response?.billingDescriptor;
            const redirect = {
                redirectionUrl: this.handleUrlSanitization(response.redirectUrl) || '',
                submitJwt: response?.postData?.JWT ? true : false,
                openInCustomTab: response?.openInCustomTab,
                redirectType: 'POST',
                new_3ds2_route: response?.successMessageInfo?.new_3ds2_route === 'Y' || response?.details?.new_3ds2_route === 'Y',
            };
            this.depositRedirectService.updateDespositRedirectInfo(redirect);

            setTimeout(() => {
                this.depositRedirectService.redirectToThirdParty(this.handleResponse);
            });
        } else if (response?.status === ResponseStatus.REDIRECT) {
            this.loaderService.depositRedirectInProgress.set(true);
            this.depositRedirectService.depositSubmitResponse = response;
            const redirect = {
                redirectionUrl: this.handleUrlSanitization(response.redirectUrl) || '',
                openInCustomTab: response?.openInCustomTab,
                redirectType: 'GET',
                new_3ds2_route: response?.successMessageInfo?.new_3ds2_route === 'Y' || response?.details?.new_3ds2_route === 'Y',
            };
            this.depositRedirectService.updateDespositRedirectInfo(redirect);
            setTimeout(() => {
                this.depositRedirectService.redirectToThirdParty(this.handleResponse);
            });
        } else if (response?.status === ResponseStatus.PENDING) {
            if (this.QDType === QDType.GVCBETSLIP) {
                this.depositRedirectService.depositSubmitResponse = response;
                this.depositRedirectService.checkDepositStatus(
                    this.depositRedirectService?.depositSubmitResponse?.transactionId,
                    this.handleResponse,
                );
            }
        } else if (response?.status === ResponseStatus.THREEDS2_REDIRECT) {
            response.instrumentNumber = this.optionListService?.selectedOption?.instrumentNumber
                ? this.optionListService?.selectedOption?.instrumentNumber.toLowerCase()
                : '';
            response.type = this.optionListService?.selectedOption?.paymentMethodInfo?.type.toLowerCase();
            response.paymentMethod = this.optionListService?.selectedOption?.paymentMethodInfo?.paymentMethod;
            response.feeCollected =
                this.depositInfo?.amountCharged !== '' && this.depositInfo?.amountInAccountCurrency !== ''
                    ? parseFloat(this.depositInfo?.amountCharged) - parseFloat(this.depositInfo?.amountInAccountCurrency)
                    : 0;
            response.amountInAccountCurrency = this.depositInfo?.amountInAccountCurrency ? this.depositInfo?.amountInAccountCurrency : '';
            response.amount.value = response?.amount && response?.amount?.value ? (Math.round(response?.amount?.value * 100) / 100).toFixed(2) : '';
            response.transactionCurrency = this.depositInfo?.amountInfo?.nativeCurrency;
            const exchangeRate = parseFloat(this.getAmountInAccountCurrency());
            response.exchangeRate = (1 / exchangeRate).toFixed(2);
            response.amountInTxnCurrency = this.depositInfo?.amountInfo?.nativeAmountString ? this.depositInfo?.amountInfo?.nativeAmountString : '';
            response.amount.currencyCode = response?.accountCurrency ? response?.accountCurrency : response?.amount?.currencyCode;
            response.billingDescriptor = response?.billingDescriptor ? response?.billingDescriptor : response.billingDescriptor;
            this.submitResponse = response;
            this.loaderService.depositRedirectInProgress.set(true);
            this.loaderService.isLoading.next(true);
            this.depositRedirectService.depositSubmitResponse = response;
            const redirect = {
                redirectionUrl: this.handleUrlSanitization(response.redirectionUrl) || '',
                redirectionData: response?.postData?.threeDSMethodData,
            };
            this.depositRedirectService.updateDespositRedirectInfo(redirect);
            this.submit3DSMethodData(redirect);
            this.depositRedirectService.check3dDepositStatus(() => {
                this.depositRedirectService.getRedirectData(this.handleRedirectionResponse);
            });
        } else if (response?.status === ResponseStatus.REDIRECT_BANK) {
            this.loaderService.depositRedirectInProgress.set(true);
            this.depositRedirectService.depositSubmitResponse = response;
            const redirect = {
                redirectionUrl: this.handleUrlSanitization(response.redirectUrl) || '',
                creq: response?.postData?.creq,
                submitCreq: true,
                openInCustomTab: response?.openInCustomTab,
                redirectType: 'POST',
                new_3ds2_route: response?.successMessageInfo?.new_3ds2_route === 'Y' || response?.details?.new_3ds2_route === 'Y',
            };
            this.depositRedirectService.updateDespositRedirectInfo(redirect);
            setTimeout(() => {
                this.depositRedirectService.redirectToThirdParty(this.handleResponse);
            });
        } else {
            this.loaderService.depositRedirectInProgress.set(false);
            this.loaderService.isLoading.next(false);
            this.successMessageData.eventData.success = false;
            this.postMessageToParent(this.successMessageData, '*');
            this.behaviorData.isFullStakeAmountNotMet.next(true);
            if (response?.errorResponse?.errorCode) {
                const errorCode = response.errorResponse.errorCode;
                this.store.setErrorCode(+errorCode);
                if (errorCode != '168') this.behaviorData.changeCVV2('');
                if (errorCode == '301') {
                    // this.behaviorData.changebwinPLkycverification(response?.errorResponse);
                    // this.behaviorData.changeHideInputPage(true);
                } else if (errorCode == '302') {
                    // this.behaviorData.changeHideInputPage(true);
                } else if (
                    (errorCode && ['168', '169', '171', '170'].includes(errorCode)) ||
                    (!['AFFORDABILITY_LIMIT_GROSS_EXCEEDED', 'AFFORDABILITY_LIMIT_GROSS_TO_BE_EXCEEDED'].includes(
                        response.errorResponse.errorMessage,
                    ) &&
                        errorCode === '178')
                ) {
                    if (errorCode === '168') {
                        this.store.setAdaInteractionMessage(true);
                        this.gtmService.raiseEventForUnusualDeposit(
                            'unusualDepositInteractionMessage',
                            'betlsip quick deposit',
                            this.depositInfo?.amountInfo?.nativeAmountString,
                        );
                    } else if (
                        errorCode === '178' &&
                        !['AFFORDABILITY_LIMIT_GROSS_EXCEEDED', 'AFFORDABILITY_LIMIT_GROSS_TO_BE_EXCEEDED'].includes(
                            response?.errorResponse?.errorMessage,
                        )
                    ) {
                        this.optionListService.redirectToMainCashier();
                    } else if (errorCode === '169') {
                        // this.behaviorData.changeInPlayBreak(true);
                    } else if (errorCode === '171') {
                        //TODO: check and remove it
                        // this.behaviorData.changeSessionTimeoutStatus(true);
                    } else if (errorCode === '170') {
                        if (response?.errorResponse?.errorMessage == 'arc_deposit_decline_intervention_message') {
                            this.store.setArcDepositBlockStatus(response?.userARCDeclinedDepositStatus);
                            // this.behaviorData.changeArcDepositBlockStatus(response?.userARCDeclinedDepositStatus);
                            const ccData = { payment: { error: '' } };
                            ccData.payment.error = 'maximum allowed decline deposits reached - deposit ability blocked for 24h';
                            this.gtmService.raiseComponentEvent('paymentError', ccData);
                        } else if (response?.errorResponse?.errorMessage == 'arc_deposit_decline_interaction_message') {
                            this.gtmService.raiseEventForArcUnusualDeposit('unusualArcDepositInteractionMessage', 'betslip quick deposit');
                        }
                    }
                } else if (
                    errorCode == ErrorCode.PaypalBad_Funding &&
                    ['2046 : INSUFFICIENT_FUNDS', '2046 : TRANSACTION_REFUSED', '2046 : INSTRUMENT DECLINED'].includes(
                        response?.errorResponse?.errorMessage,
                    )
                ) {
                    if (['2046 : TRANSACTION_REFUSED', '2046 : INSTRUMENT DECLINED'].includes(response?.errorResponse?.errorMessage)) {
                        this.behaviorData?.changePaypalBadFunding(true);
                    }
                    this.paypalDirectBadFunding = response?.errorResponse;
                } else if (
                    errorCode === ErrorCode.maxFailDepEnhancementErrorCode &&
                    response?.errorResponse?.templateName == ErrorTemplate.maxFailDepEnhancementErrorTemplate &&
                    response?.errorResponse?.additionalData?.userMaxFailDepStatus
                ) {
                    this.behaviorData.changeMaxFailDepEnhancement.next(response?.errorResponse?.additionalData?.userMaxFailDepStatus);
                }
            } else {
                this.behaviorData.isFullStakeAmountNotMet.next(true);
            }

            this.showDepositFailure.set(true);
            this.showDepositSuccess.set(false);
            this.successMessageData.eventData.success = false;
            this.postMessageToParent(this.successMessageData, '*');
        }
        if (response?.errorResponse) {
            this.handleErrorResponse(response);
        }
        this.resizeCashier();
    }

    handleUrlSanitization(url: string) {
        return this.sanitizer.sanitize(SecurityContext.URL, this.sanitizer.bypassSecurityTrustUrl(url));
    }

    handleRedirectionResponse(response: any) {
        if (response?.status === ResponseStatus.SUCCESS) {
            this.dataService.data = response;
            this.submitResponse.status = ResponseStatus.SUCCESS;
            this.optionListService.reversalExists = false;
            this.postSuccessMessageToParent(this.getAmountInAccountCurrency(), true);
            this.stakeAmountService.retrieveAmountRequiredToMeetStake(this.showRequiredView.bind(this));
            if (response?.errorResponse) {
                this.handleErrorResponse(response);
            }
        } else if (response?.status === ResponseStatus.REDIRECT_BANK) {
            const redirect = {
                redirectionUrl: this.handleUrlSanitization(response.redirectUrl) || '',
                creq: response?.redirectData?.redirectPostData,
                submitCreq: true,
                openInCustomTab: response?.openInCustomTab,
                redirectType: 'POST',
                new_3ds2_route: response?.successMessageInfo?.new_3ds2_route === 'Y' || response?.details?.new_3ds2_route === 'Y',
            };
            this.depositRedirectService.updateDespositRedirectInfo(redirect);
            setTimeout(() => {
                this.depositRedirectService.redirectToThirdParty(this.handleResponse);
            });
        } else {
            this.behaviorData.isFullStakeAmountNotMet.next(true);
            this.successMessageData.eventData.success = false;
            this.postMessageToParent(this.successMessageData, '*');
            this.depositRedirectService.checkDepositStatus(this.depositRedirectService?.depositSubmitResponse?.transactionId, this.handleResponse);
        }
    }

    submit3DSMethodData(data: any) {
        const iframe: any = document.getElementById('threeDS2redirect') as HTMLIFrameElement;

        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        iframeDoc.write(iframeCode);
        (<HTMLFormElement>iframeDoc.getElementById('threeDSMethodDataForm')).action = data?.redirectionUrl;
        (<HTMLFormElement>iframeDoc.getElementById('threeDSMethodData'))['value'] = data?.redirectionData;
        (<HTMLFormElement>iframeDoc.getElementById('threeDSMethodDataForm')).submit();
    }

    submitDeposit(depositSubmitReq: any = this.depositInfo) {
        this.depositSubmitRequest = depositSubmitReq;
        delete this.depositSubmitRequest.paymentAccountInfo.cvv2;
        this.setGaClientAndSessionInfo();
        depositSubmitReq.details['betslipQD'] = 'Y';
        const queryString = '?language=' + this.dataService.sessionInfo?.LANG_ID + '&source=QD';
        this.apiService
            .post(UrlConstants.DEPOSIT_SUBMIT + queryString, this.depositSubmitRequest)
            .pipe(
                map(
                    (response: HttpResponse<any>) => {
                        this.handleResponse(response);
                    },
                    (rejected: HttpResponse<any>) => {
                        this.handleResponse(rejected);
                    },
                ),
            )
            .subscribe(() => this.isDataLoading.set(false));
    }

    setGaClientAndSessionInfo() {
        this.gaSessionId = this.trackingValueService.gaSessionId;
        this.gaClientId = this.trackingValueService.gaClientId;
        if (this.depositInfo?.details) {
            this.depositInfo.details.GA_CLIENT_ID = this.gaClientId;
            this.depositInfo.details.GA_SESSION_ID = this.gaSessionId;
        } else {
            this.depositInfo.details = { GA_CLIENT_ID: this.gaClientId, GA_SESSION_ID: this.gaSessionId };
        }
    }

    setCurrencyConversion(selectedOption: QDInstrument) {
        this.currencyConversionMap = {};
        const accountCurrency = selectedOption?.paymentMethodInfo?.minDepositLimit?.currencyCode?.toUpperCase();
        selectedOption.currencyInfo.currencyList.forEach((currency) => {
            const conversionKey = currency.toUpperCase() + '_TO_' + accountCurrency;
            const value = 1 / this.conversionRates[conversionKey];
            this.currencyConversionMap[currency] = currency === accountCurrency ? value : value.toFixed(4);
        });
    }

    prefillNavAmount(amountToBePrefilled: any) {
        const selectedOption = this.optionListService?.selectedOption;
        if (selectedOption?.paymentMethodInfo?.paymentMethod.toUpperCase() !== AppConstants.FLASH1VOUCHER) {
            let prefilledAmount: string | number;
            if (amountToBePrefilled != '' && !isNaN(amountToBePrefilled)) {
                prefilledAmount = this.prefillInDecimal ? parseFloat(amountToBePrefilled).toFixed(2) : parseFloat(amountToBePrefilled);
            } else {
                prefilledAmount = amountToBePrefilled;
            }
            this.depositInfo.amountInfo.nativeAmountString = prefilledAmount;
        }
        this.behaviorData.changeEnteredAmount(this.depositInfo.amountInfo.nativeAmountString);
        this.getAmountInAccountCurrency();
        this.currency = this.stakeAmountService?.currency; // TODO: check and remove it
        this.fundsNeeded = this.stakeAmountService.getAmountToBePopulated(null, this.conversionRates);
        this.behaviorData.isBetSuspended.next(this.stakeAmountService?.data?.betSuspended);
        this.behaviorData.isPriceChanged.next(this.stakeAmountService?.isPriceChanged);
        this.showBonusInput = amountToBePrefilled > 0 ? true : false;
    }

    convertAmountToTxnCurrency(pendingAmount: any, roundUpTo: any) {
        const txnCurrency = this.depositInfo?.amountInfo?.nativeCurrency?.toUpperCase();
        const accCurrency = this.optionListService?.selectedOption?.paymentMethodInfo?.minDepositLimit?.currencyCode?.toUpperCase();
        const minLimit = this.optionListService?.selectedOption?.paymentMethodInfo?.minDepositLimit?.value;
        const maxLimit = this.optionListService?.selectedOption?.paymentMethodInfo?.maxDepositLimit?.value;
        const providerBalance = this.optionListService?.selectedOption?.accountBalance;
        let exchangeRate = 1;
        if (!pendingAmount || pendingAmount === '' || (txnCurrency !== accCurrency && !this.isConversionMapLoaded)) {
            return '';
        }

        if (txnCurrency !== accCurrency) {
            const key = txnCurrency + '_TO_' + accCurrency;
            exchangeRate = this.conversionRates[key];
        }

        if (
            providerBalance &&
            parseFloat(providerBalance.toString()) < parseFloat(maxLimit.toString()) &&
            parseFloat(pendingAmount) > parseFloat(providerBalance.toString())
        ) {
            return Math.floor(parseFloat(providerBalance.toString()) / exchangeRate).toFixed(2);
        }

        if (parseFloat(pendingAmount) < parseFloat(minLimit?.toString())) {
            return Math.ceil(parseFloat(minLimit?.toString()) / exchangeRate).toFixed(2);
        }

        if (parseFloat(pendingAmount) > parseFloat(maxLimit?.toString())) {
            return Math.floor(parseFloat(maxLimit?.toString()) / exchangeRate).toFixed(2);
        }
        if ('ceil' === roundUpTo) {
            return Math.ceil(parseFloat(pendingAmount) / exchangeRate).toFixed(2);
        } else if ('floor' === roundUpTo) {
            return Math.floor(parseFloat(pendingAmount) / exchangeRate).toFixed(2);
        } else {
            return (parseFloat(pendingAmount) / exchangeRate).toFixed(2);
        }
    }

    resizeCashier() {
        setTimeout(() => {
            const isMobile = this.deviceService.isMobile;
            const orientation = window?.outerHeight > window?.innerWidth ? 'portrait' : 'landscape';
            let height = this.utilService.outerHeight('.make-deposit');

            if (isMobile) {
                height = orientation === 'landscape' ? 115 : Math.min(height, 550);
            } else {
                height = Math.max(height, 200);
            }

            this.postMsg(Math.ceil(height));
        }, 100);
    }
    postMsg(height: any) {
        if (this.QDType == QDType.GVCBETSLIP) {
            if (this.oldHeight != height) {
                this.oldHeight = height;
                this.sendGvcResizeMessage(height);
            }
        } else {
            this.postMessageToParent('action=resize&width=320&height=' + (height + 13), '*');
        }
    }

    sendGvcResizeMessage(height: any) {
        if (height > 100) {
            const resizeData = {
                eventName: 'windowResize',
                eventData: {
                    height: height,
                },
            };
            this.postMessageToParent(resizeData, '*');
        }
    }
    openCashier() {
        const message = {
            eventName: 'CashierPage',
            eventData: {
                name: 'deposit',
            },
        };
        this.postMessageToParent(message, '*');
    }

    postOpenEventToLCG() {
        setTimeout(() => {
            const isMobile = this.deviceService.isMobile;
            const orientation = window?.outerHeight > window?.innerWidth ? 'portrait' : 'landscape';
            let height = this.utilService.outerHeight('.make-deposit');

            if (isMobile) {
                height = orientation === 'landscape' ? 115 : Math.min(height, 275);
            } else {
                height = Math.max(height, 200);
                height = Math.ceil(height);
            }

            this.postMessageToParent(`action=open&width=320&height=${height + 13}`, '*');
        }, 100);
    }

    postSuccessMessageToParent(amount: any, quickJourney: any) {
        if (this.QDType === QDType.GVCBETSLIP) {
            this.successMessageData.eventData.amount = Math.round(parseFloat(amount) * 100);
            this.successMessageData.eventData.quickJourney = quickJourney;
            this.gtmService.raiseEventWithSource(
                'deposit',
                'DepositSuccess',
                this.optionListService?.selectedOption?.paymentMethodInfo?.paymentMethod,
                'Betslip',
            );
            this.postMessageToParent(this.successMessageData, '*');
        }
    }

    postMessageToParent(message: any, target: any) {
        this.betslipqdMessageService.sendOutput(message);
        // window.parent.postMessage(message, target);
        if (this.dataService.sessionInfo?.channelId === 'IN') {
            window.addEventListener(message, target);
        }
    }
    keyPressNumbersDecimal(event: any) {
        const charCode = event.which ? event.which : event.keyCode;
        if (charCode !== 46 && charCode > 31 && (charCode < 48 || charCode > 57)) {
            event.preventDefault();
            return false;
        }
        return true;
    }
    //register Gpay
    registerCardDetails(url: string, cardDetails: any, cardToken?: any): any {
        if (!this.isNullOrEmpty(url) && url !== 'token') {
            return this.apiService.postPgwRequest(url, cardDetails, cardToken).subscribe();
        } else {
            return false;
        }
    }

    isNullOrEmpty(str: string | null | undefined): boolean {
        return !str || str.trim() === '';
    }
    // ApplePay  Deposit  Call
    async applePayDepositSubmit(depositSubmitReq: DepositSubmitRequest) {
        this.setGaClientAndSessionInfo();
        this.depositSubmitRequest = depositSubmitReq;
        // this.setWorldPaySessionFor3ds(depositSubmitReq);
        const tempToken: string = sessionStorage.getItem('tempToken') || '';
        depositSubmitReq.paymentAccountInfo.tempToken = tempToken;
        const depositSubmitUrl = UrlConstants.DEPOSIT_SUBMIT_URI;
        return await this.apiService
            .post(depositSubmitUrl + `?language=${this.dataService.sessionInfo.LANG_ID}&source=PG`, depositSubmitReq)
            .toPromise()
            .then(
                (data: any) => {
                    this.handleResponse(data);
                    if (data?.status === 'SUCCESS') {
                        return data;
                    }
                },
                (error: any) => {
                    error;
                },
            );
    }
    handleErrorResponse(data: any) {
        const depositSubmitResponse = data;
        let amountToBePrefilled;
        if (depositSubmitResponse?.errorResponse) {
            const errorCode = depositSubmitResponse.errorResponse.errorCode;
            const errorMessage = depositSubmitResponse.errorResponse.errorMessage;
            const allowedAmt = depositSubmitResponse.errorResponse.allowedAmt?.value;
            if (errorCode === '182' && depositSubmitResponse?.errorResponse?.errorMessage == 'GROUPBASED_MONTHLY_LIMIT_TO_BE_EXCEEDED') {
                this.prefillInDecimal = true;
                amountToBePrefilled = depositSubmitResponse?.errorResponse?.allowedAmt?.value;
                this.prefillNavAmount(this.convertAmountToTxnCurrency(amountToBePrefilled, 'floor'));
            } else if (errorCode === '971') {
                this.prefillInDecimal = true;
                amountToBePrefilled = depositSubmitResponse.errorResponse.revisedNativeAmount.toString();
                this.prefillNavAmount(this.convertAmountToTxnCurrency(amountToBePrefilled, 'floor'));
            } else if (errorCode === '503' && errorMessage === 'KYC_LIMIT_TO_BE_EXCEEDED') {
                const kycRemainingLimit = depositSubmitResponse.kycCheckResponse?.kycRemainingLimit?.value;
                amountToBePrefilled = kycRemainingLimit - this.getFeeAmount(kycRemainingLimit);
                this.prefillInDecimal = true;
                if ('DEU' === depositSubmitResponse?.kycCheckResponse?.jurisdiction) {
                    this.prefillNavAmount(this.convertAmountToTxnCurrency(amountToBePrefilled, null));
                } else {
                    this.prefillNavAmount(this.convertAmountToTxnCurrency(amountToBePrefilled, 'floor'));
                }
            } else if (
                (['177', '178', '183'].includes(errorCode) &&
                    ['AFFORDABILITY_LIMIT_GROSS_TO_BE_EXCEEDED', 'GROUPBASED_MONTHLY_LIMIT_GROSS_TO_BE_EXCEEDED'].includes(errorMessage)) ||
                (errorCode == '179' && errorMessage == 'MILESTONE_SGQ_LIMIT_GROSS_TO_BE_EXCEEDED') ||
                (errorCode == '180' && errorMessage == 'MILESTONE_SELF_CERT_LIMIT_GROSS_TO_BE_EXCEEDED') ||
                (errorCode == '181' &&
                    ['DEPOSIT_12MONTHS_LIMIT_GROSS_BEA_TO_BE_EXCEEDED', 'DEPOSIT_12MONTHS_LIMIT_GROSS_TO_BE_EXCEEDED'].includes(
                        errorMessage?.toUpperCase(),
                    ))
            ) {
                amountToBePrefilled = allowedAmt - this.getFeeAmount(allowedAmt);
                let amount: any = this.convertAmountToTxnCurrency(amountToBePrefilled, null);
                if ((amount * 100) % 100 == 0) {
                    amount = (amount * 100) / 100;
                    this.prefillInDecimal = false;
                    this.prefillNavAmount(amount);
                } else {
                    this.prefillInDecimal = true;
                    this.prefillNavAmount(amount);
                }
            } else if (
                ['783', '794'].includes(errorCode) &&
                errorMessage === 'INSTRUMENT_DEPOSIT_LIMIT_TO_BE_EXCEEDED' &&
                depositSubmitResponse.errorResponse.revisedAmount
            ) {
                const instrumentDepositLimit = depositSubmitResponse.errorResponse.revisedAmount?.value;
                amountToBePrefilled = instrumentDepositLimit - this.getFeeAmount(instrumentDepositLimit);
                this.prefillInDecimal = true;
                this.prefillNavAmount(this.convertAmountToTxnCurrency(amountToBePrefilled, 'floor'));
            } else if (
                (errorCode === '152' &&
                    ['deposit_12months_limit_to_be_exceeded', 'deposit_12months_limit_to_be_exceeded_bea'].includes(errorMessage?.toLowerCase())) ||
                (errorCode == '189' && errorMessage?.toLowerCase() == 'hardstop_o80_limit_to_be_exceeded') ||
                (errorCode == '190' && errorMessage?.toLowerCase() == 'hardstop_o80_gross_limit_to_be_exceeded') ||
                (errorCode == '191' && errorMessage?.toUpperCase() == 'AFF_L5/6_LIMIT_U25_TO_BE_EXCEEDED') ||
                (errorCode == '192' && errorMessage?.toUpperCase() == 'AFF_L5/6_LIMIT_A25_TO_BE_EXCEEDED') ||
                (['166', '167'].includes(errorCode) && errorMessage?.toUpperCase() == 'AFFORDABILITY_LIMIT_TO_BE_EXCEEDED') ||
                (errorCode == '193' && errorMessage?.toUpperCase() == 'AFF_L5/6_GROSSLIMIT_U25_TO_BE_EXCEEDED') ||
                (errorCode == '194' && errorMessage?.toUpperCase() == 'AFF_L5/6_GROSSLIMIT_A25_TO_BE_EXCEEDED') ||
                (['195', '196', '197'].includes(depositSubmitResponse?.errorResponse?.errorCode) &&
                    ['FRA_LIMIT_UNDER25_TO_BE_EXCEEDED', 'FRA_LIMIT_OVER25_TO_BE_EXCEEDED', 'FRA_LIMIT_OVER80_TO_BE_EXCEEDED'].includes(
                        depositSubmitResponse?.errorResponse?.errorMessage?.toUpperCase(),
                    )) ||
                (['160', '161'].includes(errorCode) && errorMessage?.toUpperCase() == 'AML_LIMIT_TO_BE_EXCEEDED')
            ) {
                amountToBePrefilled = allowedAmt - this.getFeeAmount(allowedAmt);
                this.prefillInDecimal = true;
                this.prefillNavAmount(this.convertAmountToTxnCurrency(amountToBePrefilled, null));
            } else if (
                (errorCode === '155' && errorMessage?.toLowerCase() === 'deposit_30days_limit_to_be_exceeded') ||
                errorMessage?.toLowerCase() === 'aml_limit_to_be_exceeded' ||
                (errorCode == '184' && errorMessage == 'MAX_LOSS_LIMIT_MONTHLY_TO_BE_EXCEEDED') ||
                (ErrorCode.SvocPurchaseLimits.includes(errorCode) && depositSubmitResponse?.errorResponse?.allowedAmt?.value > 0)
            ) {
                amountToBePrefilled = allowedAmt - this.getFeeAmount(allowedAmt);
                this.prefillInDecimal = true;
                this.prefillNavAmount(this.convertAmountToTxnCurrency(amountToBePrefilled, null));
            } else if (errorCode === '176' && errorMessage === 'MILESTONE_SELF_CERT_LIMIT_TO_BE_EXCEEDED') {
                amountToBePrefilled = allowedAmt - this.getFeeAmount(allowedAmt);
                this.prefillInDecimal = true;
                this.prefillNavAmount(this.convertAmountToTxnCurrency(amountToBePrefilled, null));
            } else if (errorCode === '175' && errorMessage === 'MILESTONE_SGQ_LIMIT_TO_BE_EXCEEDED') {
                amountToBePrefilled = allowedAmt - this.getFeeAmount(allowedAmt);
                this.prefillInDecimal = true;
                this.prefillNavAmount(this.convertAmountToTxnCurrency(amountToBePrefilled, null));
            } else if (errorCode === '172' && errorMessage?.toUpperCase() === 'TU_DEPOSIT_LIMIT_TO_BE_EXCEEDED') {
                amountToBePrefilled = allowedAmt - this.getFeeAmount(allowedAmt);
                this.prefillInDecimal = true;
                this.prefillNavAmount(this.convertAmountToTxnCurrency(amountToBePrefilled, null));
            } else if (['173', '174'].includes(errorCode) && errorMessage?.toUpperCase() === 'HARDSTOP_MONTHLY_LIMIT_TO_BE_EXCEEDED') {
                amountToBePrefilled = allowedAmt - this.getFeeAmount(allowedAmt);
                this.prefillInDecimal = true;
                this.prefillNavAmount(this.convertAmountToTxnCurrency(amountToBePrefilled, null));
            } else if (['DEPOSIT_EXCEEDS_BUDGET', '275'].includes(errorCode) && errorMessage === 'DEPOSIT_EXCEEDS_BUDGET') {
                if (parseFloat(depositSubmitResponse.errorResponse?.remainingAmount) > 0) {
                    amountToBePrefilled = depositSubmitResponse.errorResponse.remainingAmount;
                    this.prefillInDecimal = true;
                    this.prefillNavAmount(this.convertAmountToTxnCurrency(amountToBePrefilled, null));
                }
            }
        } else if (this.stakeAmountService.getAmountToBePopulated(this.depositInfo?.amountInfo?.nativeCurrency, this?.conversionRates)) {
            const roundUpTo = this.QDType === QDType.GVCBETSLIP ? 'ceil' : 'none';
            this.prefillInDecimal = true;
            this.prefillNavAmount(
                this.convertAmountToTxnCurrency(
                    this.stakeAmountService.getAmountToBePopulated(this.depositInfo?.amountInfo?.nativeCurrency, this.conversionRates),
                    roundUpTo,
                ),
            );
        }
        const paymentMethod = this.optionListService?.selectedOption?.paymentMethodInfo?.paymentMethod;
        this.gtmService.raiseEvent('quickDepositLoad', paymentMethod?.toLowerCase() + 'Input', paymentMethod);
    }
}
